import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { Badge } from "../../../../components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import {
    ArrowLeft,
    Loader2,
    Mail,
    Send,
    Trash2,
} from "lucide-react";
import { useToast } from "../../../../hooks/use-toast";
import "../TenantSettings.css";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import {
    deleteSmtpConfiguration,
    fetchSmtpConfiguration,
    saveSmtpConfiguration,
    sendSmtpTestEmail,
    TenantSmtpConfigurationRequest,
} from "../Api/smtpConfigurationApi";

export default function SmtpConfigurationPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const userEmail = useUserStore((state) => state.user.email);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [hasStoredPassword, setHasStoredPassword] = useState(false);

    const [form, setForm] = useState<TenantSmtpConfigurationRequest>({
        host: "",
        port: 587,
        username: "",
        password: "",
        fromEmail: "",
        fromDisplayName: "",
        useTls: true,
        useSsl: false,
        enabled: false,
    });
    const [testRecipient, setTestRecipient] = useState(userEmail || "");

    useEffect(() => {
        if (!tenantName) return;

        const loadConfiguration = async () => {
            setLoading(true);
            try {
                const config = await fetchSmtpConfiguration(tenantName);
                setHasStoredPassword(Boolean(config.hasPassword));
                setForm({
                    host: config.host || "",
                    port: config.port || 587,
                    username: config.username || "",
                    password: "",
                    fromEmail: config.fromEmail || "",
                    fromDisplayName: config.fromDisplayName || "",
                    useTls: config.useTls ?? true,
                    useSsl: config.useSsl ?? false,
                    enabled: config.enabled ?? false,
                });
            } catch (error) {
                console.error("Failed to load SMTP configuration:", error);
                toast({
                    title: "Load Failed",
                    description: "Unable to load SMTP configuration.",
                    variant: "destructive",
                });
            } finally {
                setLoading(false);
            }
        };

        loadConfiguration();
    }, [tenantName, toast]);

    const updateField = (key: keyof TenantSmtpConfigurationRequest, value: string | number | boolean) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        if (!tenantName) return;

        if (!form.host.trim() || !form.fromEmail.trim()) {
            toast({
                title: "Missing Information",
                description: "SMTP host and from email are required.",
                variant: "destructive",
            });
            return;
        }

        if (!hasStoredPassword && !form.password?.trim()) {
            toast({
                title: "Missing Password",
                description: "SMTP password is required for a new configuration.",
                variant: "destructive",
            });
            return;
        }

        setSaving(true);
        try {
            const saved = await saveSmtpConfiguration(tenantName, {
                ...form,
                host: form.host.trim(),
                fromEmail: form.fromEmail.trim(),
                username: form.username?.trim() || undefined,
                fromDisplayName: form.fromDisplayName?.trim() || undefined,
                password: form.password?.trim() || undefined,
            });
            setHasStoredPassword(Boolean(saved.hasPassword));
            setForm((prev) => ({ ...prev, password: "" }));
            toast({
                title: "SMTP Configuration Saved",
                description: saved.enabled
                    ? "Tenant emails will use your SMTP server."
                    : "Configuration saved. Enable it to use SMTP instead of SendGrid.",
            });
        } catch (error) {
            console.error("Failed to save SMTP configuration:", error);
            toast({
                title: "Save Failed",
                description: "Unable to save SMTP configuration.",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!tenantName || !testRecipient.trim()) return;

        setTesting(true);
        try {
            await sendSmtpTestEmail(tenantName, testRecipient.trim());
            toast({
                title: "Test Email Sent",
                description: `A test message was sent to ${testRecipient.trim()}.`,
            });
        } catch (error) {
            console.error("Failed to send SMTP test email:", error);
            toast({
                title: "Test Failed",
                description: "Unable to send test email. Verify your SMTP settings and save first.",
                variant: "destructive",
            });
        } finally {
            setTesting(false);
        }
    };

    const handleDelete = async () => {
        if (!tenantName) return;

        try {
            await deleteSmtpConfiguration(tenantName);
            setHasStoredPassword(false);
            setForm({
                host: "",
                port: 587,
                username: "",
                password: "",
                fromEmail: "",
                fromDisplayName: "",
                useTls: true,
                useSsl: false,
                enabled: false,
            });
            toast({
                title: "SMTP Configuration Removed",
                description: "Tenant emails will fall back to SendGrid.",
            });
        } catch (error) {
            console.error("Failed to delete SMTP configuration:", error);
            toast({
                title: "Delete Failed",
                description: "Unable to delete SMTP configuration.",
                variant: "destructive",
            });
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => navigate("/tenantSettings")}
                                className="settings-back-button"
                                aria-label="Back to settings"
                                title="Back to settings"
                                data-testid="button-back-general-settings"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-smtp-configuration-title">
                                    SMTP Configuration
                                </h1>
                                <p className="text-muted-foreground">
                                    Configure a custom SMTP server for outbound tenant emails. When disabled or not configured, SendGrid is used.
                                </p>
                            </div>
                        </div>
                        <Badge variant={form.enabled ? "default" : "secondary"}>
                            {form.enabled ? "SMTP Active" : "SendGrid Fallback"}
                        </Badge>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading SMTP configuration...
                </div>
            ) : (
                <div className="space-y-6 max-w-3xl">
                    <Card className="border-border/40 rounded-2xl">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                Server Settings
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-base font-medium">Use custom SMTP</Label>
                                    <p className="text-sm text-muted-foreground">
                                        When enabled, tenant emails use this SMTP server instead of SendGrid.
                                    </p>
                                </div>
                                <Switch
                                    checked={form.enabled}
                                    onCheckedChange={(checked) => updateField("enabled", checked)}
                                    data-testid="switch-smtp-enabled"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="smtp-host">SMTP Host</Label>
                                    <Input
                                        id="smtp-host"
                                        value={form.host}
                                        onChange={(e) => updateField("host", e.target.value)}
                                        placeholder="smtp.company.com"
                                        data-testid="input-smtp-host"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="smtp-port">Port</Label>
                                    <Input
                                        id="smtp-port"
                                        type="number"
                                        value={form.port}
                                        onChange={(e) => updateField("port", Number(e.target.value) || 587)}
                                        data-testid="input-smtp-port"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="smtp-username">Username</Label>
                                    <Input
                                        id="smtp-username"
                                        value={form.username}
                                        onChange={(e) => updateField("username", e.target.value)}
                                        placeholder="smtp-user@company.com"
                                        data-testid="input-smtp-username"
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="smtp-password">
                                        Password
                                    </Label>
                                    <Input
                                        id="smtp-password"
                                        type="password"
                                        value={form.password}
                                        onChange={(e) => updateField("password", e.target.value)}
                                        placeholder={hasStoredPassword ? "••••••••" : "Enter SMTP password"}
                                        data-testid="input-smtp-password"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-6">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={form.useTls}
                                        onCheckedChange={(checked) => updateField("useTls", checked)}
                                        data-testid="switch-smtp-tls"
                                    />
                                    <Label>Use TLS (STARTTLS)</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={form.useSsl}
                                        onCheckedChange={(checked) => updateField("useSsl", checked)}
                                        data-testid="switch-smtp-ssl"
                                    />
                                    <Label>Use SSL</Label>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-border/40 rounded-2xl">
                        <CardHeader>
                            <CardTitle>Sender Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="smtp-from-email">From Email</Label>
                                    <Input
                                        id="smtp-from-email"
                                        value={form.fromEmail}
                                        onChange={(e) => updateField("fromEmail", e.target.value)}
                                        placeholder="noreply@company.com"
                                        data-testid="input-smtp-from-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="smtp-from-name">Display Name</Label>
                                    <Input
                                        id="smtp-from-name"
                                        value={form.fromDisplayName}
                                        onChange={(e) => updateField("fromDisplayName", e.target.value)}
                                        placeholder="WhiteSwan Security"
                                        data-testid="input-smtp-from-name"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-border/40 rounded-2xl">
                        <CardHeader>
                            <CardTitle>Test Configuration</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="smtp-test-recipient">Test recipient</Label>
                                <Input
                                    id="smtp-test-recipient"
                                    value={testRecipient}
                                    onChange={(e) => setTestRecipient(e.target.value)}
                                    placeholder="admin@company.com"
                                    data-testid="input-smtp-test-recipient"
                                />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Save your configuration first, then send a test email using the saved SMTP settings.
                            </p>
                        </CardContent>
                    </Card>

                    <div className="flex flex-wrap gap-3">
                        <Button onClick={handleSave} disabled={saving} data-testid="button-save-smtp">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Save Configuration
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleTest}
                            disabled={testing || !hasStoredPassword}
                            className="gap-2"
                            data-testid="button-test-smtp"
                        >
                            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Send Test Email
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={!hasStoredPassword}
                            className="gap-2"
                            data-testid="button-delete-smtp"
                        >
                            <Trash2 className="w-4 h-4" />
                            Remove Configuration
                        </Button>
                    </div>
                </div>
            )}

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove SMTP Configuration</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will delete your custom SMTP settings. Outbound emails will use SendGrid again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
